import { c as _c } from "react/compiler-runtime";
import { assistantSessionsPath, buildSessionsScopeParam } from "@/lib/assistant-sessions";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { toast } from "sonner";
import { useDebouncedValue } from "./useDebouncedValue";
import { useApi } from "./useApi";
import { useOrgFromPathname } from "./useOrgFromPathname";
import { orgInstanceSlug, useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
import type { CachedAssistantSession, SearchedSession } from "./useAssistantSessionCache";
const MIN_QUERY_LENGTH = 2;
// AssistantController::listSessions rejects longer `search` values with a 422.
const MAX_QUERY_LENGTH = 200;
const DEBOUNCE_MS = 300;
const STALE_TIME = 60 * 1000;
const MAX_SEARCH_RESULTS = 8;
interface AssistantSessionSearchData {
  orgName: string;
  instanceSlug: string;
  term: string;
  mine: CachedAssistantSession[];
  all: CachedAssistantSession[];
}
export function useAssistantSessionSearch(searchTerm, userId) {
  const $ = _c(40);
  const api = useApi();
  const activeGithubOrg = useOrgFromPathname();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== connectedGithubOrgs?.installations) {
    t0 = connectedGithubOrgs?.installations.find(installation => installation?.account?.login === activeGithubOrg)?.codesmith_enabled ?? false;
    $[0] = activeGithubOrg;
    $[1] = connectedGithubOrgs?.installations;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const codesmithEnabled = t0;
  const t1 = activeGithubOrg ?? null;
  let t2;
  if ($[3] !== connectedGithubOrgs || $[4] !== t1) {
    t2 = orgInstanceSlug(connectedGithubOrgs, t1);
    $[3] = connectedGithubOrgs;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const instanceSlug = t2;
  let t3;
  if ($[6] !== searchTerm) {
    t3 = searchTerm.trim();
    $[6] = searchTerm;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const trimmed = t3;
  const debouncedTerm = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const searchable = !!activeGithubOrg && !!instanceSlug && codesmithEnabled;
  const queryActive = trimmed.length >= MIN_QUERY_LENGTH && trimmed.length <= MAX_QUERY_LENGTH;
  const enabled = searchable && debouncedTerm.length >= MIN_QUERY_LENGTH && debouncedTerm.length <= MAX_QUERY_LENGTH;
  let t4;
  if ($[8] !== activeGithubOrg || $[9] !== debouncedTerm || $[10] !== instanceSlug) {
    t4 = ["assistantSessionSearch", instanceSlug, activeGithubOrg, debouncedTerm];
    $[8] = activeGithubOrg;
    $[9] = debouncedTerm;
    $[10] = instanceSlug;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== activeGithubOrg || $[13] !== api || $[14] !== debouncedTerm || $[15] !== instanceSlug || $[16] !== userId) {
    t5 = async () => {
      const orgName = activeGithubOrg;
      const resolvedInstanceSlug = instanceSlug;
      const query = encodeURIComponent(debouncedTerm);
      const org = {
        orgName,
        instanceSlug: resolvedInstanceSlug
      };
      const scope = buildSessionsScopeParam(null, org);
      const sessionsPath = assistantSessionsPath(org);
      const baseUrl = `${sessionsPath}?status=active&search=${query}&limit=${MAX_SEARCH_RESULTS}${scope}`;
      const allResponse = await api.get(baseUrl);
      const all = (allResponse.data.sessions || []) as CachedAssistantSession[];
      const ownMatchesMayBeTruncated = all.length >= MAX_SEARCH_RESULTS && all.some(session => session.user_id !== userId);
      const mine = ownMatchesMayBeTruncated ? ((await api.get(`${baseUrl}&ownership=mine`)).data.sessions || []) as CachedAssistantSession[] : all.filter(session_0 => session_0.user_id === userId);
      return {
        orgName,
        instanceSlug: resolvedInstanceSlug,
        term: debouncedTerm,
        mine,
        all
      };
    };
    $[12] = activeGithubOrg;
    $[13] = api;
    $[14] = debouncedTerm;
    $[15] = instanceSlug;
    $[16] = userId;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== enabled) {
    t6 = {
      enabled,
      keepPreviousData: true,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      onError: _temp
    };
    $[18] = enabled;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const {
    data,
    isFetching
  } = useQuery(t4, t5, t6);
  let t7;
  if ($[20] !== activeGithubOrg || $[21] !== data || $[22] !== instanceSlug || $[23] !== trimmed || $[24] !== userId) {
    bb0: {
      if (!data || data.orgName !== activeGithubOrg || data.instanceSlug !== instanceSlug) {
        let t8;
        if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = [];
          $[26] = t8;
        } else {
          t8 = $[26];
        }
        t7 = t8;
        break bb0;
      }
      let t8;
      if ($[27] !== data.mine) {
        t8 = new Set(data.mine.map(_temp2));
        $[27] = data.mine;
        $[28] = t8;
      } else {
        t8 = $[28];
      }
      const mineIds = t8;
      const mine_0 = data.mine.map(_temp3);
      let t9;
      if ($[29] !== mineIds) {
        t9 = session_3 => !mineIds.has(session_3.id);
        $[29] = mineIds;
        $[30] = t9;
      } else {
        t9 = $[30];
      }
      let t10;
      if ($[31] !== userId) {
        t10 = session_4 => ({
          ...session_4,
          isMine: userId != null && session_4.user_id === userId
        });
        $[31] = userId;
        $[32] = t10;
      } else {
        t10 = $[32];
      }
      const others = data.all.filter(t9).map(t10);
      const merged = [...mine_0, ...others].slice(0, MAX_SEARCH_RESULTS);
      if (data.term === trimmed) {
        t7 = merged;
        break bb0;
      }
      const terms = trimmed.toLowerCase().split(/\s+/);
      t7 = merged.filter(session_5 => {
        const fields = [session_5.title, ...(session_5.managed_prs ?? []).flatMap(_temp4)].map(_temp5);
        return terms.every(term => fields.some(field_0 => field_0.includes(term)));
      });
    }
    $[20] = activeGithubOrg;
    $[21] = data;
    $[22] = instanceSlug;
    $[23] = trimmed;
    $[24] = userId;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const sessions = t7;
  const isDebouncing = searchable && queryActive && trimmed !== debouncedTerm;
  let t8;
  if ($[33] !== queryActive || $[34] !== searchable || $[35] !== sessions) {
    t8 = searchable && queryActive ? sessions : [];
    $[33] = queryActive;
    $[34] = searchable;
    $[35] = sessions;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  const t9 = searchable && queryActive && (isDebouncing || enabled && isFetching);
  let t10;
  if ($[37] !== t8 || $[38] !== t9) {
    t10 = {
      sessions: t8,
      isLoading: t9
    };
    $[37] = t8;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
}
function _temp5(field) {
  return field?.toLowerCase() ?? "";
}
function _temp4(pr) {
  return [pr.title, pr.head_branch, pr.repository];
}
function _temp3(session_2) {
  return {
    ...session_2,
    isMine: true
  };
}
function _temp2(session_1) {
  return session_1.id;
}
function _temp() {
  return toast.error("Failed to search sessions", {
    id: "assistant-session-search"
  });
}